import { jsPlumb } from "jsplumb";

import ApplicationController from "./application_controller"

const ARROW_CONNECTION_GAP = 15;

export default class extends ApplicationController {

  static targets = [
    'currentWordType', 
    'spellingLinkagesModal',
    'spellingTextField'
  ]

  initialize() {
    window['typeo_form'] = this;
    // Close modal that was open on previous page
    this.removeAllModalBackdrops();
    this.spellingContainer               = this.currentWordTypeTarget;
    this.spellingContainerData           = this.spellingContainer.dataset;
    this.typing_for                      = this.spellingContainerData.currentWordType;
    this.isWordMisspelled                = this.spellingContainerData.isWordMisspelled === "true";
    this.isCurrentWordMultiWord          = this.spellingContainerData.isMultiCurrentWord === "true";
    this.isCurrentWordInParts            = this.spellingContainerData.isInPartsCurrentWord === "true";
    this.isWordCategoryEntry             = this.spellingContainerData.isWordCategoryEntry === "true";
    this.noLinkedWordsAreMarkedAsParts   = this.spellingContainerData.combinedWordNoLinkedWordsMarkedAsPart === 'true';
    this.allLinkedWordsAreMarkedAsParts  = this.spellingContainerData.combinedWordAllLinkedWordsMarkedAsPart === 'true';
    this.linkedCombinedWordHasComponents = this.spellingContainerData.linkedCombinedWordHasComponents === 'true';
    this.currentWordPoints               = JSON.parse(this.spellingContainerData.currentWordPoints || '[]');
    this.previousOttomanSpellingValue    = "";
    // Session-only Infl./Drvl. snapshots for the first spelling while C.Frg. is on (keyed by lane).
    this.compoundFragmentFirstSpellingMorphologicalSnapshots = {};

    this.removeComponentToggleFromTypingFieldsAbovePrimarySpelling();
    
    // Remove molecule icon from primary spellings of in_parts words if their combined words has components.
    this.removeInPartsWordsPrimarySpellingMoleculeIconIfCombinedWordHasComponents();
    this.filterEntrySpellingsInLinkageModalBasedOnCurrentWordType();
    this.addEventListenersToLinkagesModal();
    
    // Initially update the molecule icon color for fields where all spelling relationships have been handled.
    this.updateAllMoleculeIconsColor();
    // Hide molecule icons where no linkable relations exist
    this.updateMoleculeIconsVisibility(); 
    this.handleVisibilityOfArrowLinkingCheckboxes();
    this.applyCompoundFragmentToggleUpdates();
    this.handleCombineFragmentsButtonVisibility();
  }

  connect() {
    // setting existing spellings current text field values
    this.setExistingSpellingValues();

    // Initialize author toggles on first visible alternate at load
    this.handleAuthorLangTogglesVisibilityOnAlternates();
    this.syncSameToggleInLinkagePannel();
    this.syncUnrelatedToggleInLinkagePannel();
    this.updateAllMoleculeIconsColor();
    this.updateValueCardMorphologicalRelationshipTogglesVisibility();
    this.syncActiveSpellingWordTogglesVisibility();
  }

  // Primary stack when not misspelled; corrected stack when misspelled (word-level Frg./fragment/affix toggles).
  activeTypingValueContainer() {
    const isMisspelled = this.isCurrentWordMisspelled();
    const containerId = isMisspelled ? 'js-corrected-otto-fields-container' : 'js-primary-otto-fields-container';

    return this.spellingContainer?.querySelector(`#${containerId}`) ||
           document.getElementById(containerId) ||
           this.spellingContainer;
  }

  // Swap primary vs corrected word-level toggle groups when the misspelled checkbox changes at runtime.
  syncActiveSpellingWordTogglesVisibility(isMisspelled = this.isCurrentWordMisspelled()) {
    if (!this.spellingContainer) return;

    this.spellingContainer.querySelectorAll('.js-active-spelling-word-toggles-primary').forEach(el => {
      el.classList.toggle('d-none', isMisspelled);
    });
    this.spellingContainer.querySelectorAll('.js-active-spelling-word-toggles-corrected').forEach(el => {
      el.classList.toggle('d-none', !isMisspelled);
    });

    this.syncWordLevelTogglesFromHiddenFields();
    this.handleFragmentTypeTogglesVisibility();
  }

  // Keep the visible primary/corrected toggle row in sync with word-level hidden fields.
  syncWordLevelTogglesFromHiddenFields() {
    const wordFieldsContainer = this.spellingContainer;
    const activeContainer = this.activeTypingValueContainer();
    if (!wordFieldsContainer || !activeContainer) return;

    const fragmentType = wordFieldsContainer.querySelector('.js-fragment-type-fallback')?.value || '';
    activeContainer.querySelectorAll('.js-fragment-type-visual-toggle').forEach(toggle => {
      toggle.checked = toggle.dataset.fragmentTypeValue === fragmentType;
    });

    const affixClassification = wordFieldsContainer.querySelector('.js-affix-classification-fallback')?.value || '';
    activeContainer.querySelectorAll('.js-affix-classification-visual-toggle').forEach(toggle => {
      toggle.checked = toggle.dataset.affixClassificationValue === affixClassification;
    });
  }
  
  handleCombineFragmentsButtonVisibility() {
    const allOttoFields    = this.allSpellingFields();
    const firstOttoField   = allOttoFields[0];
    const firstFieldToggle = firstOttoField?.querySelector('.js-marked-as-compound-fragment-visual-toggle');

    if (!firstFieldToggle) return;

    const shouldShowButton = firstFieldToggle.checked && allOttoFields.length === 1;

    // Initial state: if Compound Fragment toggle is ON, show Combine Fragmets button if there is only 1 text field
    this.updateCombineFragmentsButtonVisibility(shouldShowButton);
  }

  updateCombineFragmentsButtonVisibility(showButton) {
    const combineFragmentsButtons = document.querySelectorAll('.js-combine-compound-fragments-btn');

    combineFragmentsButtons.forEach(btn => {
      btn.classList.toggle('d-none', !showButton);
    });
  }

  applyCompoundFragmentFieldAdjustments(e) {
    const isChecked    = e.currentTarget.checked;
    const currentField = e.currentTarget.closest('.js-entries-input');

    if (isChecked) {
      // Hide the same toggles that are hidden when part_toggle is checked
      const togglesContainerToHide = currentField.querySelectorAll('.js-toogles-to-hide-if-marked-as-part');
      togglesContainerToHide.forEach(container => {
        container.classList.add('d-none');
      });

      // Hide only the current field's spelling linkage checkbox
      const currentFieldCheckbox = currentField.querySelector('.js-spelling-linkage-checkbox-container');
      if (currentFieldCheckbox) {
        currentFieldCheckbox.classList.add('d-none');
      }
      
      this.handleCombineFragmentsButtonVisibility();
    } else {
      // Show the toggles again
      const togglesContainerToHide = currentField.querySelectorAll('.js-toogles-to-hide-if-marked-as-part');
      togglesContainerToHide.forEach(container => {
        container.classList.remove('d-none');
      });
      
      // Show the current field's spelling linkage checkbox
      const currentFieldCheckbox = currentField.querySelector('.js-spelling-linkage-checkbox-container');
      if (currentFieldCheckbox) {
        currentFieldCheckbox.classList.remove('d-none');
      }

      this.handleCombineFragmentsButtonVisibility();
    }

    const typeoFieldsContainer = document.getElementById('typeo-fields-container');
    if (typeoFieldsContainer && typeof typeo_form !== 'undefined') {
      // C.Frg. hides/clears CPG-252 toggles on the first spelling container only; restores on turn-off.
      typeo_form.updateValueCardMorphologicalRelationshipTogglesVisibility(typeoFieldsContainer);
    }
  }

  // Add this new method
  markSecondFieldAsPrimary() {
    const allVisibleFields    = this.allSpellingFields();
    const secondSpellingField = allVisibleFields[1];

    if (secondSpellingField) {
      const primaryRadio = secondSpellingField.querySelector('.js-marked-as-primary-radio-btn');
      if (primaryRadio && !primaryRadio.checked) {
        primaryRadio.checked = true;
        primaryRadio.dispatchEvent(new Event('click', { bubbles: true }));
      }
    }
  }

  getNextTextField(currentField) {
    // Find the next text field after the current one
    const allFields = this.allSpellingFields();
    const currentIndex = allFields.indexOf(currentField);
    
    if (currentIndex !== -1 && currentIndex < allFields.length - 1) {
      return allFields[currentIndex + 1];
    }
    
    return null;
  }

  applyCompoundFragmentToggleUpdates() {
    const compoundFragmentToggles = this.spellingContainer.querySelectorAll('.js-marked-as-compound-fragment-visual-toggle');

    compoundFragmentToggles.forEach(toggle => {
      if (toggle.checked) {
        const currentField = toggle.closest('.js-entries-input');

        this.applyCompoundFragmentFieldAdjustments({ currentTarget: toggle });
      }
    }); 
  }

  handleVisibilityOfArrowLinkingCheckboxes() {
    let isMisspelled = this.isCurrentWordMisspelled();

    if (isMisspelled) {
      const spellingsFields = [...document.querySelectorAll('.js-entries-input:not(.js-corrected-entries-input)')];

      spellingsFields.forEach(spellingField => {
        const linkageCheckbox = spellingField.querySelector('.js-spelling-linkage-checkbox');
        if (linkageCheckbox) {
          linkageCheckbox.classList.add('d-none');
        }
      });
    }
  } 

  setExistingSpellingValues(){
    this.previousSpellingValues = new Map();

    this.spellingTextFieldTargets.forEach(spellingTextField => {
      this.previousSpellingValues.set(spellingTextField.id, spellingTextField.value);
    });
  }
  
  removeAllModalBackdrops(){
    document.querySelectorAll('.modal-backdrop').forEach(el => el.remove());
    document.body.classList.remove('modal-open');
  }

  removeComponentToggleFromTypingFieldsAbovePrimarySpelling(e) {
    const ottomanTypingFields = $('.js-entries-input');

    Array.from(ottomanTypingFields).some(spellingField => {

      spellingField.querySelector('.js-marked-as-component-toggle-container')?.classList.add('d-none');
      return spellingField.classList.contains('js-primary-spelling');
    });
  }

  removeInPartsWordsPrimarySpellingMoleculeIconIfCombinedWordHasComponents(e) {
    if (!this.isCurrentWordInParts || this.isCurrentWordMultiWord) { return };

    if (this.linkedCombinedWordHasComponents) {
      this.removePrimarySpellingMoleculeIcon();
    }
  }

  handlePrimarySpellingMoleculeIcon(isComponentToggleChecked) {
    if (isComponentToggleChecked) {
      this.displayPrimarySpellingMoleculeIcon();
    } else {
      if (!this.isCurrentWordInParts || !this.linkedCombinedWordHasComponents) { return };
      const checkedComponentTogglesCount = this.spellingContainer.querySelectorAll('input.js-marked-as-component-toggle:checked').length;
      
      if (checkedComponentTogglesCount == 0) {
        this.removePrimarySpellingMoleculeIcon();
      }
    }
  }

  displayPrimarySpellingMoleculeIcon(e){
    const primarySpellingField       = this.spellingContainer.querySelector('.js-primary-spelling');
    const primarySpellingLinkageIcon = primarySpellingField?.querySelector('.js-entry-words-primary-spellings-icon');
    primarySpellingLinkageIcon?.classList.remove('collapse');
  }

  removePrimarySpellingMoleculeIcon(e){
    const primarySpellingLinkageIcon = this.spellingContainer.querySelector('.js-entry-words-primary-spellings-icon:not(.collapse)');
    if (!primarySpellingLinkageIcon) return;
    
    const primarySpellingField       = this.currentTypingFieldContainer(primarySpellingLinkageIcon);
    
    primarySpellingLinkageIcon.classList.add('collapse');
  }

  destroyEntryCategorySpellingLinkagesOfPartToggleSpellingField(e) {
    const partToggle = e.currentTarget;
    const partToggleSpellingField = this.currentTypingFieldContainer(partToggle);
    const currentWordSpellingLinkagesFields = partToggleSpellingField.querySelectorAll('.js-spelling-linkages');

    if (partToggle.checked == true) {
      currentWordSpellingLinkagesFields.forEach(currentWordSpellingLinkagesField => {
        this.resetEnrtyCategoryWordSpellingLinkages(currentWordSpellingLinkagesField);
      });
    } else {
      currentWordSpellingLinkagesFields.forEach(currentWordSpellingLinkagesField => {
        if (!currentWordSpellingLinkagesField.classList.contains('collapse')){
          this.updateDestroyField(currentWordSpellingLinkagesField, false);
        }
      });
    }
  }

  handleSpellingLinkagesMoleculeIcon(selectedPrimarySpellingContainer) {
    // Remove spelling linkage icon from all ottoman spellings fields 
    $('.js-entry-words-primary-spellings-icon').each(function() {
      if (!$(this).hasClass('collapse')) {
        $(this).addClass('collapse');
      }
    });

    const spellingLinkageIcon      = selectedPrimarySpellingContainer.querySelector('.js-entry-words-primary-spellings-icon');
    const linkagesModal            = selectedPrimarySpellingContainer.querySelector('.js-spelling-linkage-modal');
    const linkageSpellingContainer = linkagesModal.querySelector('.js-entry-words-primary-seplling-linkage-container')
    const emptyLinkageSpelling     = linkageSpellingContainer.innerHTML.trim() === '';
      
    // If there is no spelling to be linked, ensure icon is hidden
    if (emptyLinkageSpelling) {
      spellingLinkageIcon?.classList.add('collapse');
      return;
    }

    if (spellingLinkageIcon == null) return;

    // Display spelling linkage icon in selected primary ottoman spelling field
    if (!this.isCurrentWordInParts || !this.linkedCombinedWordHasComponents){
      this.displayPrimarySpellingMoleculeIcon();
    }
  }

  // Determines whether a spelling field has any linkable content in its modal
  hasMoleculeModalLinkableContent(spellingField) {
    const modal = this.spellingLinkageModal(spellingField);
    if (!modal) return false;

    // Check if there are any visible relationship toggles in the modal
    const hasRelationshipToggles = modal.querySelector('.js-spelling-linkages-container:not(.collapse) .js-entry-word-spellings-relationship-toggle');
    if (hasRelationshipToggles) return true;

    // Check if there are any existing component linkages
    const hasComponentLinkages = modal.querySelector('.js-component-spelling-linkage:not(.collapse)');
    if (hasComponentLinkages) return true;

    return false;
  }

  // Hides molecule icons for spelling fields that have no linkable items
  updateMoleculeIconsVisibility() {
    if (!this.hasCurrentWordTypeTarget) return;

    const spellingFields = this.currentWordTypeTarget.querySelectorAll('.js-entries-input.js-spelling-field-container') || [];

    spellingFields.forEach(spellingField => {
      const moleculeIcon = spellingField.querySelector('.js-entry-words-primary-spellings-icon');
      if (!moleculeIcon) return;

      const isPrimaryChecked   = spellingField.querySelector('.js-marked-as-primary-radio-btn')?.checked === true;
      const isComponentChecked = spellingField.querySelector('.js-marked-as-component-checkbox')?.checked === true;

      if (!(isPrimaryChecked || isComponentChecked)) return;

      const hasContent = this.hasMoleculeModalLinkableContent(spellingField);
      moleculeIcon.classList.toggle('collapse', !hasContent);
    });
  }
  
  // In case current word is multi this method 
  // - removes all in_parts words spellings from primary spelling linkage modal
  // when  current word is in parts this method 
  // - removes all multi words spellings from linkage modal
  filterEntrySpellingsInLinkageModalBasedOnCurrentWordType() {
    if (this.isCurrentWordMultiWord) {
      const primarySpellingField = $('.js-primary-spelling')[0];
      this.hideAllInPartsWordsSpellingFromLinkageModal(primarySpellingField);
    } else if (this.isCurrentWordInParts) {
      this.removeEntryMultiSpellingsFromLinkageModal();
    }
  }

  addEventListenersToLinkagesModal() {
    const spellingLinkagesModal = this.spellingLinkagesModalTargets;

    this.spellingLinkagesModalTargets.forEach(spellingLinkagesModal => {
      spellingLinkagesModal.addEventListener("hidden.bs.modal", this.updateMoleculeIconColorOfCurrentSpellingField.bind(this));
      // Track user interactions with Same toggle so we don't auto-toggle again
      spellingLinkagesModal.addEventListener('change', (e) => {
        const target = e.target;
        // Only treat as user interaction if event is trusted (not programmatic)
        if (!e.isTrusted) return;
        if (target && target.classList && target.classList.contains('js-marked-as-same-toggle')) {
          const linkageContainer = target.closest('.js-spelling-linkages-container');
          if (linkageContainer) {
            linkageContainer.dataset.userSameToggled = 'true';
          }
        }
      });
      // Track user interactions with Unrelated toggle so we don't auto-toggle again
      spellingLinkagesModal.addEventListener('change', (e) => {
        const target = e.target;
        // Only treat as user interaction if event is trusted (not programmatic)
        if (!e.isTrusted) return;
        if (target && target.classList && target.classList.contains('js-marked-as-unrelated-value-toggle')) {
          const linkageContainer = target.closest('.js-spelling-linkages-container');
          if (linkageContainer) {
            linkageContainer.dataset.userUnrelatedToggled = 'true';
          }
        }
      });
    });
  }

  // This method updates all molecule icons color to red if at least one relationship toggle is enabled in all spelling relation fields of respective spelling field
  updateAllMoleculeIconsColor() {
    if (!this.hasCurrentWordTypeTarget) return; // Early return to avoid error 'missing target element'

    const spellingFields = this.currentWordTypeTarget.querySelectorAll('.js-entries-input.js-spelling-field-container') || [];

    spellingFields.forEach( spellingField => {
      const moleculeIcon = spellingField.querySelector('.js-entry-words-primary-spellings-icon'); 

      if (moleculeIcon && !moleculeIcon.classList.contains('collapse')) {
        this.updateMoleculeIconColor(spellingField);
      }
    });
  }
  
  // This method updates current spelling field molecule icon color to red if at least one relationship toggle is enabled in all spelling relation fields
  // and removes molecule icon color any of the spelling relationship fields has no toggle on
  updateMoleculeIconColorOfCurrentSpellingField(e) {
    const spellingLinkageModalElement = e.currentTarget;
    const spellingField               = this.currentTypingFieldContainer(spellingLinkageModalElement);

    this.updateMoleculeIconColor(spellingField);
  }

  updateMoleculeIconColor(spellingField) {
    let isAnyLinkageToggleOn = true;

    const moleculeIcon   = spellingField.querySelector('.js-entry-words-primary-spellings-icon'); 
    const linkagesFields = spellingField.querySelectorAll('.js-spelling-linkages-container:not(.collapse)');
    
    if(!moleculeIcon) { return };
    
    linkagesFields.forEach(linkagesField => {
      const spellingsLinkingtoggles = linkagesField.querySelectorAll('.js-entry-word-spellings-relationship-toggle');
      // only check next linkage if any of previous toggle is checked
      if (isAnyLinkageToggleOn == true) {
        // Checking if at least one relationship toggle is enabled in all spelling relation fields
        
        isAnyLinkageToggleOn = Array.from(spellingsLinkingtoggles).some(toggle => toggle.checked);
      }
    });

    moleculeIcon.querySelector('.js-grey-molecule').classList.toggle('collapse', isAnyLinkageToggleOn);
    moleculeIcon.querySelector('.js-red-molecule').classList.toggle('collapse', !isAnyLinkageToggleOn);
    // moleculeIcon.classList.toggle('text-danger', isAnyLinkageToggleOn);
  }

  // This method is triggered when component toggle is turned on 
  // reset all component spellings molecule color to default
  resetAllComponentSpellingsMoleculeIconColor(e){
    if (!this.isWordCategoryEntry) return;

    const componentSpellingFields = this.currentWordTypeTarget.querySelectorAll('.js-entries-input.js-spelling-field-container:not(.js-primary-spelling)');

    componentSpellingFields.forEach( componentSpellingField => {
      const moleculeIcon = componentSpellingField.querySelector('.js-entry-words-primary-spellings-icon'); 

      if (moleculeIcon && !moleculeIcon.classList.contains('collapse')) {
        moleculeIcon.querySelector('.js-grey-molecule').classList.remove('collapse');
        moleculeIcon.querySelector('.js-red-molecule').classList.add('collapse');
      }
    });
  }

  // Old -> this method removes all multi words spellings from linkage modal
  // New -> this method removes current word's combined word spellings from linkage modal
  removeEntryMultiSpellingsFromLinkageModal() {
    // const multiWordSpellingContainers = $('.js-entry-multi-spelling');
    const multiWordSpellingContainers = $('.js-current-word-combined-entry-word');

    Array.from(multiWordSpellingContainers).some(multiWordSpellingContainer => {
      multiWordSpellingContainer.classList.add('collapse');
      this.resetEnrtyCategoryWordSpellingLinkages(multiWordSpellingContainer);
    });
  }

  hasSameOttomanExceptEndingWithHaa(firstOttomanValue, secondOttomanValue) {
    if (!firstOttomanValue || !secondOttomanValue) return false;
    if (firstOttomanValue === secondOttomanValue) return true;

    const haaVariants = ['ه', 'ە'];

    // Check if the lengths are the same
    if (Math.abs(firstOttomanValue.length - secondOttomanValue.length) !== 0) return false;

    // Check if all characters except the last one are the same
    if (firstOttomanValue.slice(0, -1) === secondOttomanValue.slice(0, -1)) {
      return haaVariants.includes(firstOttomanValue.slice(-1)) && haaVariants.includes(secondOttomanValue.slice(-1));
    }

    return false;
  }

  areSpellingFieldValuesIdenticalInSpellingLinkagesModal(formSpellingField, expalinedSpellingFieldInSpellingLinkageModal){
    const spellingLinkagesContainer    = expalinedSpellingFieldInSpellingLinkageModal.closest('.js-spelling-linkages-container');
    const explanationSpellingContainer = spellingLinkagesContainer.querySelector('.js-entry-word-spelling');
    const explanationSpellingValue     = explanationSpellingContainer.textContent.trim();
    const explanationSpellingPoints    = explanationSpellingContainer?.dataset?.points ? JSON.parse(explanationSpellingContainer.dataset.points) : null;

    const explainedSpellingValue    = formSpellingField.value;
    const explainedSpellingPoints   = this.currentWordPoints;

    return this.spellingsHaveSamePointsAndOttomanValues(explainedSpellingValue, explainedSpellingPoints, explanationSpellingValue, explanationSpellingPoints);
  }

  // This method populates current spelling value in linkages modal
  populateCurrentSpellingInLinkageModal(e){
    const spellingRelationIcon   = e.currentTarget;
    const spellingContainer      = this.currentTypingFieldContainer(spellingRelationIcon);
    const spellingIsComponent    = this.isComponentSpelling(spellingContainer);
    const formSpellingField      = spellingContainer.querySelector('.js-spelling-field');
    const formSpellingFieldValue = formSpellingField.value;
    
    const explainedSpellingFieldsInSpellingLinkageModal = spellingContainer.querySelectorAll('.js-spelling-linking-modal-explained-spelling');

    explainedSpellingFieldsInSpellingLinkageModal.forEach(explainedSpellingField => {
      explainedSpellingField.textContent = formSpellingFieldValue;
      if (spellingIsComponent) {
        const spellingsToLinkAreSame    = this.areSpellingFieldValuesIdenticalInSpellingLinkagesModal(formSpellingField, explainedSpellingField)
        const spellingLinkagesContainer = explainedSpellingField.closest('.js-spelling-linkages-container');
        
        // We do not allow linkages between similar spellings if both are of the Entry type. The explanation spelling field (shown via the molecule icon) is always of type Entry. So, if the explained spelling is also of type Entry, we remove it to avoid linking them together
        // if (( spellingsToLinkAreSame && this.isWordCategoryEntry) || spellingLinkagesContainer.dataset.isEntryCombinedWord === "true") {
        if (spellingsToLinkAreSame && this.isWordCategoryEntry) {
            spellingLinkagesContainer?.classList.add('collapse');
            this.resetEnrtyCategoryWordSpellingLinkages(spellingLinkagesContainer);
        } else {
            const spellingsLinkageFieldsContainer = this.currentTypingFieldContainer(explainedSpellingField);
            const unrelatedToggleContainer = spellingsLinkageFieldsContainer?.querySelector('.js-unrelated-toggle-container');
            unrelatedToggleContainer?.classList.remove('d-none');
        }        
      }
    });

    // Sync Same toggle state based on current value // TODO: Verify whether this is needed here
    this.updateSameToggleStateForLinkageContainers(spellingContainer, formSpellingFieldValue);
    // Also sync Unrelated toggle state based on current value
    this.updateUnrelatedToggleStateForLinkageContainers(spellingContainer, formSpellingFieldValue);
  }

  removeDuplicatePopulatedValuesFromSepllingLinkingModal(e) {
    // Replaced setTimeout with requestAnimationFrame to avoid the blinking effect that occurs when the visible div is hidden at runtime.
    requestAnimationFrame(() => {
      const spelling                = this.currentTypingFieldContainer(e.target)
      const modal                   = this.spellingLinkageModal(spelling);
      const formSpellingField       = spelling.querySelector('.js-spelling-field')
      const explainedSpellingValue  = formSpellingField.value;
      const explainedSpellingPoints = this.currentWordPoints;

      // Get component spelling explanation spellings
      const componentSpellingExplanations = modal.querySelectorAll('.js-component-spelling-explanation');

      // Get non-component spellings
      const nonComponentEntryWordSpellings = modal.querySelectorAll('.js-non-component-entry-word-spelling');

      // Remove non component spellings that have duplicate spellings as current container spelling in entry word
      // but if current word is of non-entry type, then allow linkage even if both spellings are same because
      // non-entry to entry linkage is required even if spellings are same
      nonComponentEntryWordSpellings.forEach(nonComponentEntryWordSpelling => {
        if (this.isWordCategoryEntry) {
          const explanationSpellingValue  = nonComponentEntryWordSpelling.textContent.trim();
          const explanationSpellingPoints = nonComponentEntryWordSpelling?.dataset?.points ? JSON.parse(nonComponentEntryWordSpelling.dataset.points) : null;

          const spellingsAreIdentical = this.spellingsHaveSamePointsAndOttomanValues(
            explainedSpellingValue,
            explainedSpellingPoints,
            explanationSpellingValue,
            explanationSpellingPoints
          );

          if (spellingsAreIdentical) {
            const duplicateSpellingContainer = nonComponentEntryWordSpelling.closest('.js-spelling-linkages-container');
            if(!duplicateSpellingContainer.classList.contains('js-entry-part-spelling')){
              duplicateSpellingContainer.classList.add('collapse');
              this.resetEnrtyCategoryWordSpellingLinkages(duplicateSpellingContainer);
            }
          }
        }
      });

      // Now hide non-component spellings if they duplicate any of the component spellings
      nonComponentEntryWordSpellings.forEach(spellingDiv => {
        const nonComponentSpellingValue  = spellingDiv.textContent.trim();
        const nonComponentSpellingPoints = spellingDiv?.dataset?.points ? JSON.parse(spellingDiv.dataset.points) : null;

        const hasMatchingComponentSpelling = Array.from(componentSpellingExplanations).some(componentSpellingExplanation => {
          const componentSpellingValue  = componentSpellingExplanation.textContent.trim();
          const componentSpellingPoints = this.currentWordPoints;

          const spellingsAreIdentical = this.spellingsHaveSamePointsAndOttomanValues(
            nonComponentSpellingValue,
            nonComponentSpellingPoints,
            componentSpellingValue,
            componentSpellingPoints
          );

          if (spellingsAreIdentical) {
            const spellingLinkagesContainer = spellingDiv.closest('.js-spelling-linkages-container');
            if (spellingLinkagesContainer && !spellingLinkagesContainer.classList.contains('js-entry-part-spelling')) {
              spellingLinkagesContainer.classList.add('collapse');
              this.resetEnrtyCategoryWordSpellingLinkages(spellingLinkagesContainer);
            }
            return true; // stop .some() here
          }

          return false;
        });
      });
    });
  }

  // Shows fragment-type toggles (Wrd./Prfx./Sffx./Infx./Mngs.) when Frg. or C.Frg is active;
  // clears them when both are off. Selected value is stored in a single hidden fragment_type field.
  handleFragmentTypeTogglesVisibility(e) {
    const wordFieldsContainer = this.spellingContainer;
    const activeContainer = this.activeTypingValueContainer();
    if (!wordFieldsContainer || !activeContainer) return;

    // Word-level hidden fields live on #typeo-fields-container; visual toggles on the active spelling stack only.
    const partToggleOn = this.isFragmentFlagOn(wordFieldsContainer, '.js-marked-as-part-fallback');
    const compoundFragmentToggleOn = this.isFragmentFlagOn(wordFieldsContainer, '.js-marked-as-compound-fragment-fallback');
    const shouldShow = partToggleOn || compoundFragmentToggleOn;

    activeContainer.querySelectorAll('.js-fragment-type-toggles-container').forEach(container => {
      container.classList.toggle('d-none', !shouldShow);
    });

    if (!shouldShow) {
      this.clearFragmentTypeToggles(wordFieldsContainer, activeContainer);
    }

    this.updateValueCardMorphologicalRelationshipTogglesVisibility(wordFieldsContainer);
    this.updateAffixClassificationTogglesVisibility(wordFieldsContainer, activeContainer);
  }

  isFragmentModeOn(typeoFieldsContainer = this.spellingContainer) {
    if (!typeoFieldsContainer) return false;

    // Either Frg. or C.Frg. puts the item in CPG-273 mode (not CPG-252 value-card relationships).
    return this.isPartModeOn(typeoFieldsContainer) ||
           this.isCompoundFragmentModeOn(typeoFieldsContainer);
  }

  // Word-level Frg. flag — hides/clears CPG-252 morphological toggles on all spelling containers.
  isPartModeOn(typeoFieldsContainer = this.spellingContainer) {
    if (!typeoFieldsContainer) return false;

    return this.isFragmentFlagOn(typeoFieldsContainer, '.js-marked-as-part-fallback');
  }

  // Word-level C.Frg. flag — CPG-273 applies to the fragment (first spelling) only; merged/alternates keep CPG-252.
  isCompoundFragmentModeOn(typeoFieldsContainer = this.spellingContainer) {
    if (!typeoFieldsContainer) return false;

    return this.isFragmentFlagOn(typeoFieldsContainer, '.js-marked-as-compound-fragment-fallback');
  }

  // CPG-252 morphological relationship toggles (Infl./Drvl.) do not apply to Frg. items — CPG-273 only.
  // C.Frg. hides/clears CPG-252 toggles on the first spelling container only; other spellings keep them.
  // Turning C.Frg. off restores the first spelling's prior Infl./Drvl. state for the active lane.
  // When Frg./C.Frg. is turned off again, containers are shown and CPG-252 gating resumes via typing-form.
  updateValueCardMorphologicalRelationshipTogglesVisibility(typeoFieldsContainer = this.spellingContainer) {
    if (!typeoFieldsContainer) return;

    const partModeOn = this.isPartModeOn(typeoFieldsContainer);
    const compoundFragmentModeOn = this.isCompoundFragmentModeOn(typeoFieldsContainer);
    const spellingFields = this.allSpellingFields();
    const firstSpellingField =
      spellingFields.find(field => field.classList.contains('js-first-spelling-field')) ||
      spellingFields[0] ||
      null;

    typeoFieldsContainer.querySelectorAll('.js-value-card-left-margin-container').forEach(container => {
      const spellingField = container.closest('.js-entries-input');
      const isFirstSpellingContainer = spellingField && spellingField === firstSpellingField;
      const hideRelationshipToggles = partModeOn ||
        (compoundFragmentModeOn && isFirstSpellingContainer);

      container.classList.toggle('d-none', hideRelationshipToggles);
    });

    if (partModeOn) {
      // Frg. replaces CPG-252 entirely — discard any pending C.Frg. restore snapshots.
      this.clearCompoundFragmentFirstSpellingMorphologicalSnapshots();
      this.clearValueCardMorphologicalRelationshipToggles(typeoFieldsContainer);
    } else if (compoundFragmentModeOn && firstSpellingField) {
      // Snapshot once per lane before clearing so turn-off can restore pre-C.Frg. Infl./Drvl.
      this.snapshotCompoundFragmentFirstSpellingMorphologicalToggles(firstSpellingField);
      this.clearValueCardMorphologicalRelationshipToggles(firstSpellingField);
    } else if (firstSpellingField) {
      // C.Frg. turned off — show first-spelling toggles again and restore the saved lane snapshot.
      this.restoreCompoundFragmentFirstSpellingMorphologicalToggles(firstSpellingField);
    }
  }

  // Save first-spelling Infl./Drvl. once when entering C.Frg. mode for this lane.
  // Skips if a snapshot already exists so repeated calls (e.g. fragment-type changes) do not overwrite with cleared values.
  snapshotCompoundFragmentFirstSpellingMorphologicalToggles(firstSpellingField) {
    const lane = this.morphologicalLaneForField(firstSpellingField);

    if (this.compoundFragmentFirstSpellingMorphologicalSnapshots[lane]) return;

    this.compoundFragmentFirstSpellingMorphologicalSnapshots[lane] =
      this.readMorphologicalToggleState(firstSpellingField);
  }

  // Re-apply a lane snapshot after C.Frg. is turned off, then refresh CPG-252 gating for that lane.
  restoreCompoundFragmentFirstSpellingMorphologicalToggles(firstSpellingField) {
    const lane = this.morphologicalLaneForField(firstSpellingField);
    const snapshot = this.compoundFragmentFirstSpellingMorphologicalSnapshots[lane];

    if (!snapshot) return;

    this.applyMorphologicalToggleState(firstSpellingField, snapshot);
    delete this.compoundFragmentFirstSpellingMorphologicalSnapshots[lane];
    this.scheduleMorphologicalToggleStatesUpdate({ lane });
  }

  // Drop all pending C.Frg. restore snapshots (e.g. when Frg. takes over).
  clearCompoundFragmentFirstSpellingMorphologicalSnapshots() {
    this.compoundFragmentFirstSpellingMorphologicalSnapshots = {};
  }

  readMorphologicalToggleState(scope) {
    return {
      inflectional: scope.querySelector('.js-morphological-inflectional-toggle')?.checked ?? false,
      derivational: scope.querySelector('.js-morphological-derivational-toggle')?.checked ?? false,
    };
  }

  // Dispatch change so handleMorphologicalToggleExclusivity and nested form fields stay in sync.
  applyMorphologicalToggleState(scope, { inflectional, derivational }) {
    const inflectionalToggle = scope.querySelector('.js-morphological-inflectional-toggle');
    const derivationalToggle = scope.querySelector('.js-morphological-derivational-toggle');

    if (inflectionalToggle && inflectionalToggle.checked !== inflectional) {
      inflectionalToggle.checked = inflectional;
      inflectionalToggle.dispatchEvent(new Event('change', { bubbles: true }));
    }

    if (derivationalToggle && derivationalToggle.checked !== derivational) {
      derivationalToggle.checked = derivational;
      derivationalToggle.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  clearValueCardMorphologicalRelationshipToggles(scope) {
    if (!scope) return;

    // Match CPG-252 helpers: js-morphological-inflectional-toggle / js-morphological-derivational-toggle
    scope.querySelectorAll('.js-morphological-inflectional-toggle, .js-morphological-derivational-toggle').forEach(toggle => {
      if (toggle.checked) {
        toggle.checked = false;
        toggle.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  }

  // CPG-273: fragment types that require Infl./Drvl. affix classification (matches Word::AFFIX_FRAGMENT_TYPES).
  AFFIX_FRAGMENT_TYPES = ['prefix', 'suffix', 'infix'];

  // CPG-273 UI: show word-level Infl./Drvl. toggles only when Frg./C.Frg. is on and Prfx./Sffx./Infix
  // is selected. Hides and clears toggles when user picks Wrd./Mngs. or turns off fragment mode.
  updateAffixClassificationTogglesVisibility(wordFieldsContainer = this.spellingContainer, activeContainer = this.activeTypingValueContainer()) {
    if (!wordFieldsContainer || !activeContainer) return;

    const fragmentTypeValue = wordFieldsContainer.querySelector('.js-fragment-type-fallback')?.value;
    const shouldShow = this.isFragmentModeOn(wordFieldsContainer) && this.AFFIX_FRAGMENT_TYPES.includes(fragmentTypeValue);

    activeContainer.querySelectorAll('.js-affix-classification-toggles-container').forEach(container => {
      container.classList.toggle('d-none', !shouldShow);
    });

    if (!shouldShow) {
      this.clearAffixClassificationToggles(wordFieldsContainer, activeContainer);
    }
  }

  clearAffixClassificationToggles(wordFieldsContainer = this.spellingContainer, activeContainer = this.activeTypingValueContainer()) {
    activeContainer.querySelectorAll('.js-affix-classification-visual-toggle').forEach(toggle => {
      toggle.checked = false;
    });

    const affixClassificationHiddenField = wordFieldsContainer.querySelector('.js-affix-classification-fallback');
    if (affixClassificationHiddenField) affixClassificationHiddenField.value = '';
  }

  handleAffixClassificationToggleExclusivity(e) {
    const currentToggle = e.currentTarget;
    const wordFieldsContainer = this.spellingContainer;
    const activeContainer = this.activeTypingValueContainer();
    if (!wordFieldsContainer || !activeContainer) return;

    const affixClassificationValue = currentToggle.dataset.affixClassificationValue;
    if (!affixClassificationValue) return;

    const allAffixClassificationToggles = activeContainer.querySelectorAll('.js-affix-classification-visual-toggle');
    const affixClassificationHiddenField = wordFieldsContainer.querySelector('.js-affix-classification-fallback');

    if (currentToggle.checked) {
      allAffixClassificationToggles.forEach(toggle => {
        toggle.checked = toggle.dataset.affixClassificationValue === affixClassificationValue;
      });

      if (affixClassificationHiddenField) affixClassificationHiddenField.value = affixClassificationValue;
    } else {
      allAffixClassificationToggles.forEach(toggle => {
        if (toggle.dataset.affixClassificationValue === affixClassificationValue) {
          toggle.checked = false;
        }
      });

      if (affixClassificationHiddenField) affixClassificationHiddenField.value = '';
    }
  }

  isFragmentFlagOn(typeoFieldsContainer, hiddenSelector) {
    const hiddenValue = typeoFieldsContainer.querySelector(hiddenSelector)?.value;
    return ['1', 'true'].includes(String(hiddenValue));
  }

  clearFragmentTypeToggles(wordFieldsContainer = this.spellingContainer, activeContainer = this.activeTypingValueContainer()) {
    activeContainer.querySelectorAll('.js-fragment-type-visual-toggle').forEach(toggle => {
      toggle.checked = false;
    });

    const fragmentTypeHiddenField = wordFieldsContainer.querySelector('.js-fragment-type-fallback');
    if (fragmentTypeHiddenField) fragmentTypeHiddenField.value = '';
  }

  handleFragmentTypeToggleExclusivity(e) {
    const currentToggle        = e.currentTarget;
    const wordFieldsContainer  = this.spellingContainer;
    const activeContainer      = this.activeTypingValueContainer();
    if (!wordFieldsContainer || !activeContainer) return;

    const fragmentTypeValue = currentToggle.dataset.fragmentTypeValue;
    if (!fragmentTypeValue) return;

    const allFragmentTypeToggles = activeContainer.querySelectorAll('.js-fragment-type-visual-toggle');
    const fragmentTypeHiddenField = wordFieldsContainer.querySelector('.js-fragment-type-fallback');

    // Mutual exclusivity: turning one ON turns all others OFF and sets the single fragment_type value
    if (currentToggle.checked) {
      allFragmentTypeToggles.forEach(toggle => {
        if (toggle.dataset.fragmentTypeValue === fragmentTypeValue) {
          toggle.checked = true;
        } else {
          toggle.checked = false;
        }
      });

      if (fragmentTypeHiddenField) fragmentTypeHiddenField.value = fragmentTypeValue;
    } else {
      // Turning OFF: clear selection
      allFragmentTypeToggles.forEach(toggle => {
        if (toggle.dataset.fragmentTypeValue === fragmentTypeValue) {
          toggle.checked = false;
        }
      });
      if (fragmentTypeHiddenField) fragmentTypeHiddenField.value = '';
    }

    this.updateAffixClassificationTogglesVisibility(wordFieldsContainer, activeContainer);
  }

  // Per-item save validation: reads word-level hidden fields; active stack must have fragment type selected when Frg./C.Frg. is on.
  showFragmentTypeTogglesSelectionErrorIfRequired() {
    const wordFieldsContainer = this.spellingContainer;
    const activeContainer = this.activeTypingValueContainer();
    if (!wordFieldsContainer || !activeContainer) return false;

    const partToggleOn = this.isFragmentFlagOn(wordFieldsContainer, '.js-marked-as-part-fallback');
    const compoundFragmentToggleOn = this.isFragmentFlagOn(wordFieldsContainer, '.js-marked-as-compound-fragment-fallback');

    if (!partToggleOn && !compoundFragmentToggleOn) return false;

    const fragmentTypeValue = wordFieldsContainer.querySelector('.js-fragment-type-fallback')?.value;
    const fragmentTypeSelected = fragmentTypeValue && fragmentTypeValue !== '';

    // When misspelled, ignore stale primary-row toggles — hidden field is authoritative after corrected-row edits.
    if (!fragmentTypeSelected) {
      showFlashMessage('Please select a fragment type (Wrd., Prfx., Sffx., Infx., or Mngs.) before proceeding.', 'alert');
      return true;
    }

    return false;
  }

  showComponentTogglesSelectionErrorIfRequired() {
    const primarySpellingField            = this.spellingContainer.querySelector('.js-primary-spelling');
    const primarySpellingValue            = primarySpellingField?.querySelector('.js-spelling-field').value;
    const isFirstPrimaryField             = primarySpellingField?.classList.contains('js-first-spelling-field');
    const checkedComponentToggles         = this.spellingContainer.querySelectorAll('input.js-marked-as-component-toggle:checked');
    const firstPrimaryFieldHasSpace       = isFirstPrimaryField && primarySpellingValue.trim().includes(' ');
    const someLinkedWordsAreMarkedAsParts = !this.noLinkedWordsAreMarkedAsParts && !this.allLinkedWordsAreMarkedAsParts;
    
    // In combined word case if less than 2 component spellings are selected and
    // some linked words are marked as parts OR all linkaed words are marked as parts AND 
    // first spelling marked as primary spelling with space 
    if (this.typing_for == 'combined_word' && checkedComponentToggles.length < 2 &&
          ((someLinkedWordsAreMarkedAsParts || this.allLinkedWordsAreMarkedAsParts) && firstPrimaryFieldHasSpace)
       ) {
      showFlashMessage('Please create at least two components before proceeding to the next item.', 'alert');
      return true
    }

    // if less than 2 component spellings are selected 
    if (checkedComponentToggles.length > 0 && checkedComponentToggles.length < 2){
      showFlashMessage('A Compound consists of at least two Components. Please mark at least one more value as a Component, or remove the Component label from the existing value.', 'alert');
      return true
    }
      
    return false
  }

  // This method is triggered when molecule icon is clicked
  // And handles visibility of in_parts entry category words spellings in linkage modal of primary spelling in case of current combined word
  handleVisibilityOfInPartEntrySpellingsInCombinedWordPrimarySpellingLinkageModal(e) {
    if (this.typing_for != 'combined_word') return;
    
    const spellingRelationIcon    = e.currentTarget;
    const spellingContainer       = this.currentTypingFieldContainer(spellingRelationIcon);
    const currentPrimarySpelling  = spellingContainer.querySelector('.js-spelling-field').value;
    const isFirstSpellingField    = spellingContainer.classList.contains('js-first-spelling-field');
    const isPrimarySpelling       = spellingContainer.classList.contains('js-primary-spelling');
    const checkedComponentToggles = this.spellingContainer.querySelectorAll('input.js-marked-as-component-toggle:checked');
    const isMultiWord             = checkedComponentToggles.length > 0 || this.noLinkedWordsAreMarkedAsParts || (isFirstSpellingField && isPrimarySpelling && currentPrimarySpelling.trim().includes(' '));

    if (isPrimarySpelling && isMultiWord) {
      this.hideAllInPartsWordsSpellingFromLinkageModal(spellingContainer);
      this.displayMultiWordsSpellingsFromLinkageModal(spellingContainer);
    } else if(isPrimarySpelling) {
      this.displayAllInPartSpellingsFromLinkageModal(spellingContainer);
      this.displayMultiWordsSpellingsFromLinkageModal(spellingContainer);
    } else {
      this.displayAllInPartSpellingsFromLinkageModal(spellingContainer);
    }
  }
  
  isComponentSpelling(spelling){
    const componentToggle = spelling.querySelector('.js-marked-as-component-checkbox');
    return componentToggle?.checked || false;    
  }

  getComponentSpellings(spellings) {
    // Filter only those spellings where the checkbox is checked
    const componentSpellings = spellings.filter(spelling => {
      const componentToggle = spelling.querySelector('.js-marked-as-component-checkbox');
      return componentToggle?.checked || false;
    });

    return componentSpellings;    
  }

  componentSpellingPairsToBePopulated(currentSpelling, componentSpellings) {
    // Step 1: Create pairs of currentSpelling with each componentSpelling
    const allPairs = componentSpellings.map(spelling => ({
      componentSpellingExplanation: spelling,
      currentComponentSpelling: currentSpelling
    }));

    // Step 2: Get all linked component spellings, meaning the spellings that have already been saved and are present in the modal.
    const linkedComponentElements = document.querySelectorAll('.js-component-spelling-linkage:not(.collapse)');

    // Step 3: Extract pairs from existing linked component spellings
    const existingLinkedPairs = [...linkedComponentElements].map(element => ({
      componentSpellingExplanation: element.querySelector('.js-component-spelling-explanation'),
      explainedComponentSpelling: element.querySelector('.js-explained-component-spelling')
    }));
    
    // Step 4: Filter out pairs that already exist in existingLinkedPairs to get the pairs that need to be populated in the modal.
    const componentSpellingPairsToPopulate = allPairs.filter(pair => {
      return !existingLinkedPairs.some(existingPair => {
        return existingPair.componentSpellingExplanation.textContent.trim() === pair.componentSpellingExplanation.querySelector('.js-text-field').value &&
               existingPair.explainedComponentSpelling.textContent.trim() === pair.currentComponentSpelling.querySelector('.js-text-field').value;
      });
    });
    
    return componentSpellingPairsToPopulate;
  }

  // This method is triggered when ottoman spelling text field value is updated
  // This method works only for entry type words
  // This method resets previous value of component spelling from all component spellings linkages modal 
  resetComponentSpellingPreviousTextFieldValueLinkageFromComponentLinkingModal(e) {
    if (!this.isWordCategoryEntry) return;

    const currentSpellingInputField = e.currentTarget;
    const currentSpelling           = this.currentTypingFieldContainer(currentSpellingInputField);
    const componentToggle           = currentSpelling.querySelector('input.js-marked-as-component-toggle');
    const previousSpellingValue     = this.previousSpellingValues.get(currentSpellingInputField.id) || "";

    // setting new spelling value as previous value for next time
    this.previousSpellingValues.set(currentSpellingInputField.id, currentSpellingInputField.value);
    
    // No need to reset component linkages if primary value is updated or current spelling is not component spelling.
    if (currentSpelling.classList.contains('js-primary-spelling') || !componentToggle.checked ) return;
    
    typing_form.resetComponentSpellingModalLinkages(currentSpelling, previousSpellingValue, true);
    this.resetAllComponentSpellingsMoleculeIconColor();
  }

  populateValuesInComponentSpellingTemplateFields(componentSpellingsLinkageContainer, spellingsToLink) {
    const explanationValue          = spellingsToLink.componentSpellingExplanation.querySelector('.js-text-field')?.value || "";
    const explanationSpellingTempId = spellingsToLink.componentSpellingExplanation.querySelector('.js-spelling-temp-reference-id')?.value || "";    
    const explainedValue            = spellingsToLink.currentComponentSpelling.querySelector('.js-text-field')?.value || "";

    const explanationValueDiv            = componentSpellingsLinkageContainer.querySelector('.js-component-spelling-explanation');
    const explainedValueDiv              = componentSpellingsLinkageContainer.querySelector('.js-explained-component-spelling');
    const explanationSpellingTempIdField = componentSpellingsLinkageContainer.querySelector('.js-explanation-spelling-temp-id');

    if (explanationValueDiv) {
      explanationValueDiv.textContent = explanationValue;
    }

    if (explainedValueDiv) {
      explainedValueDiv.textContent = explainedValue;
    }

    if (explanationSpellingTempIdField) {
      explanationSpellingTempIdField.value = explanationSpellingTempId;
    }    
  }

  populateComponentSpellingLinakgesFields(componentSpellingPairsToPopulate, currentSpelling) {
    const currentSpellingLinkageModal = this.spellingLinkageModal(currentSpelling);
    const buttonToAddTemplate         = currentSpellingLinkageModal.querySelector('.js-add-component-spelling-linkage-template');

    if (!buttonToAddTemplate) return;

    const targetContainer = buttonToAddTemplate.closest('.js-component-spelling-linkages-container')
      ?.querySelector('.js-component-spelling-linkages-template-container');

    if (!targetContainer) return;

    // A delay is added after clicking the button to add a new template 
    // because without this delay, attempting to access the newly added template immediately resulted in selecting non-existent element
    const populatePairOfSpellingsInModalFields = (pair) => {
      return new Promise((resolve) => {
        buttonToAddTemplate.click();

        setTimeout(() => {
          const newTemplateContainer = targetContainer.children[targetContainer.children.length - 1];

          if (newTemplateContainer) {
            this.populateValuesInComponentSpellingTemplateFields(newTemplateContainer, pair);
          }

          resolve();
        }, 100);
      });
    };

    componentSpellingPairsToPopulate.reduce((promiseChain, pair) => {
      return promiseChain.then(() => populatePairOfSpellingsInModalFields(pair));
    }, Promise.resolve());
  }

  spellingLinkageModal(spelling){
    const spellingLinkageModal = spelling.querySelector('.js-spelling-linkage-modal');
    return spellingLinkageModal;
  }

  unhideEntryWordPrimarySpellingLinkageContainer(currentSpelling) {
    const currentModal = currentSpelling.querySelector('.js-spelling-linkage-modal');
    const entryWordPrimarySpellingLinkageContainer = currentModal.querySelector('.js-entry-words-primary-seplling-linkage-container');
    
    if (entryWordPrimarySpellingLinkageContainer) {
      entryWordPrimarySpellingLinkageContainer.classList.remove('d-none');
    }
  }

  // This method removes multi-word spellings and
  // displays in-part-word spellings in the current spelling linkage modal.
  handleEntrySpellingsLinkagesVisibilityOfComponentSpelling(currentSpelling) {
    const currentSpellingLinkageModal = this.spellingLinkageModal(currentSpelling);
    
    if (!currentSpellingLinkageModal) return;
    
    const entryWordPrimarySpellingLinkageContainer = currentSpellingLinkageModal.querySelector('.js-entry-words-primary-seplling-linkage-container');

    if (entryWordPrimarySpellingLinkageContainer) {
      // collapse multi word spellings 
      // This following method call for hiding multi-word spellings has been commented out, since the updated requirement is to link them with component spellings ref# https://trello.com/c/BcI1uWh3/1885-enable-relations-between-components-and-compounds-in-molecule-panel
      // this.hideMultiWordsSpellingsFromLinkageModal(entryWordPrimarySpellingLinkageContainer);

      // collapse curent word in_parts word spellings 
      this.hideCurrentWordInPartsWordsSpellingsFromLinkageModal(entryWordPrimarySpellingLinkageContainer);
      
      // display in part words spellings
      this.displayOtherEntryCategoryInPartsWordsSpellingsFromLinkageModal(entryWordPrimarySpellingLinkageContainer);
    }
  }

  // This method populates the fields in the molecule modal for component spellings using template
  populateComponentSpellingLinkagesInLinkagesModal(e) {
    const spellingLinkageIcon = e.target;
    const currentSpelling     = spellingLinkageIcon.closest('.js-entries-input');

    if (!this.isComponentSpelling(currentSpelling)) {
      this.unhideEntryWordPrimarySpellingLinkageContainer(currentSpelling);
      return; // Exit early if spelling is not a component spelling
    }

    // remove multi word spellings and display in part word spellings in linkage modal of component spelling
    this.handleEntrySpellingsLinkagesVisibilityOfComponentSpelling(currentSpelling);

    // Get all spellings except the current spelling
    const allSpellings = this.allSpellingFields().filter(spelling => spelling !== currentSpelling);

    // Get all component spellings except the current spelling
    const componentSpellings = this.getComponentSpellings(allSpellings);

    const componentSpellingPairsToPopulate = this.componentSpellingPairsToBePopulated(currentSpelling, componentSpellings)

    // If there are unpopulated component spellings to be added to the modal, populate them in the modal
    if (componentSpellingPairsToPopulate.length > 0) {
      this.populateComponentSpellingLinakgesFields(componentSpellingPairsToPopulate, currentSpelling);
    }    
  }

  openEntryWordLinkageSourcesModal(e) {
    e.preventDefault();

    // Get the modal ID from the button's data-bs-target
    let modalId = e.currentTarget.getAttribute("data-bs-target");
    let modalElement = document.querySelector(modalId);

    this.handleSourcesModalPosition(e, modalElement);

    // Open the second modal manually
    const sourcesModal = new bootstrap.Modal(modalElement)
    modalElement.querySelector('.modal-content').classList.add("spelling-linkage-sources-content")
    sourcesModal.show();
  }

  handleSourcesModalPosition(e, sourcesModalElement){
    let sourcesModalDialog = sourcesModalElement.querySelector('.modal-dialog');

    // Get sources '+' button position
    const sourcesButtonRect = e.currentTarget.getBoundingClientRect();
    
    // Position modal-dialog near the button
    sourcesModalDialog.style.position = "absolute";
    
    // check if source is last visible on screen 
    if (window.innerHeight - sourcesButtonRect.bottom < 200) {
      sourcesModalDialog.style.top = `${sourcesButtonRect.top - 350}px`;
    } else {
      sourcesModalDialog.style.top = `${sourcesButtonRect.bottom - 40}px`;
    }

    sourcesModalDialog.style.left = `${sourcesButtonRect.left - 650}px`;
  }

  handleSpellingsVisibilityInLinkageModals(e) {
    const componentPartToggle = e.target;
    const isComponentToggleChecked = componentPartToggle.checked;
    this.handleMultiAndInPartWordSpellingsVisibiliyInLinkageModal(isComponentToggleChecked);
  }

  handleMultiAndInPartWordSpellingsVisibiliyInLinkageModal(isComponentToggleChecked) {
    const checkedComponentToggles = this.spellingContainer.querySelectorAll('input.js-marked-as-component-toggle:checked');

    // show in parts/multi words spelling if user unchecked all components toggles
    // hide in parts/multi words spelling if user checked first component toggle
    if ( checkedComponentToggles.length == 0 && isComponentToggleChecked == false || 
         checkedComponentToggles.length == 1 && isComponentToggleChecked == true
        ) {

      if (this.isCurrentWordInParts) {
        this.handleVisbilityOfMultiEntryWordsInLinkageModal(isComponentToggleChecked);
      }

      this.handleVisbilityOfInPartEntryWordsSpellingLinkageModal(isComponentToggleChecked);
    }
  }

  // this method handles visibility of all multi words spellings in linkages modal based on component toggle stage
  handleVisbilityOfMultiEntryWordsInLinkageModal(isComponentToggleChecked){
    const multiEntrySpellingContainers = $('.js-entry-multi-spelling');

    Array.from(multiEntrySpellingContainers).some(multiEntrySpellingContainer => {
      
      if (isComponentToggleChecked) {
        multiEntrySpellingContainer.classList.remove('collapse');
        this.updateDestroyField(multiEntrySpellingContainer, false)

      } else {
        multiEntrySpellingContainer.classList.add('collapse');
        this.resetEnrtyCategoryWordSpellingLinkages(multiEntrySpellingContainer);
      }
    });
  }

 // this method handles visibility of all in_parts words spellings in linkages modal based on component toggle stage
  handleVisbilityOfInPartEntryWordsSpellingLinkageModal(isComponentToggleChecked){
    this.handleVisibilityOfInPartWordsInPrimarySpellingLinkages(isComponentToggleChecked);
    this.handleVisibilityOfInPartWordsInComponentSpellingLinkages(isComponentToggleChecked);
  }

  checkIfAllSpellingsHaveValidLinkages(event){
    const pageSetId = event.target.dataset.pageSetId;

    $.ajax({
      url: '/page_set/typeo/get_validity_status_of_all_spelling_linkages',
      type: "GET",
      data: { page_set: pageSetId },   
      success: (response) => {
        if (response.valid) {
          const modal = new bootstrap.Modal(document.getElementById(`submit_pageset_modal-${pageSetId}`));
          modal.show();
        } else {
          this.addFlashMessage(response.error);
        }
      }
    }) ;   
  }
 
  // this method handles visibility of all in_parts words spellings in primary spelling linkages modal based on component toggle stage
  // in case component toggle is on hide ann in_parts words spellings from primary speling linkages modal
  // for toggle off we have two cases one for individual word one for combined word
  // In case of individual word if all component toggles are off display  all in_parts words spellings
  // In case of combined word 
  //  - If all component toggles are off check for multi word based on space in first primary spelling
  //  - if none linked component words are marked as parts then word is always multi
  //  - In case of 'all' or 'some' linked component words are marked as parts then word is multi multi if first spelling is primary spelling with space 
  //  - if combined word is multi word then hide all in_parts words spellings
  //  - if combined word is not multi word then display all in_parts words spellings
  handleVisibilityOfInPartWordsInPrimarySpellingLinkages(isComponentToggleChecked){
    const primarySpellingField = $('.js-primary-spelling')[0];

    if (isComponentToggleChecked) {
      this.hideAllInPartsWordsSpellingFromLinkageModal(primarySpellingField)
    } else {
      if (this.typing_for == 'combined_word'){
        const primarySpellingValue = primarySpellingField.querySelector('.js-spelling-field').value;
        const isFirstPrimaryField  = primarySpellingField.classList.contains('js-first-spelling-field');

        const isMultiWord = this.noLinkedWordsAreMarkedAsParts || 
                            (isFirstPrimaryField && primarySpellingValue.trim().includes(' '));
        
        if (!isMultiWord) {
          this.displayAllInPartSpellingsFromLinkageModal(primarySpellingField);
        }
      } else {
        this.displayAllInPartSpellingsFromLinkageModal(primarySpellingField);
      }
    }
  }

  // this method handles visibility of all in_parts words spellings in component spelling linkages modals based on component toggle stage
  // if component toggle is on then hide current words in_parts words spellings and also hide multi words spellings
  // if component toggle is off then reset all  toggles of component modals 
  handleVisibilityOfInPartWordsInComponentSpellingLinkages(isComponentToggleChecked){
    const spellingLinkageModals = $('.js-spelling-linkage-modal');

    Array.from(spellingLinkageModals).some(spellingLinkageModal => {
      const spellingContainer = this.currentTypingFieldContainer(spellingLinkageModal);
      // return in case of primary spelling spelling modal
      if (spellingContainer.classList.contains('js-primary-spelling')) return false;
      
      if (isComponentToggleChecked){
        this.hideCurrentWordInPartsWordsSpellingsFromLinkageModal(spellingLinkageModal);
        // This following method call for hiding multi-word spellings has been commented out, since the updated requirement is to link them with component spellings ref# https://trello.com/c/BcI1uWh3/1885-enable-relations-between-components-and-compounds-in-molecule-panel
        // this.hideMultiWordsSpellingsFromLinkageModal(spellingLinkageModal);
      } else {
        // reset all spellings 
        this.resetEnrtyCategoryWordSpellingLinkages(spellingLinkageModal);
      }
    });
  }

  // this method hides in_part spellings in current spelling field linkages modal
  hideAllInPartsWordsSpellingFromLinkageModal(spellingField) {
    this.hideCurrentWordInPartsWordsSpellingsFromLinkageModal(spellingField);
    this.hideOtherEntryCategoryInPartsWordsSpellingsFromLinkageModal(spellingField)
  }

  // this method hides current word in_parts words spellings
  hideCurrentWordInPartsWordsSpellingsFromLinkageModal(spellingField) {
    const currentWordInPartEntrySpellingContainers = spellingField.querySelectorAll('.js-current-word-part-spelling');

    // hide current word in_parts words spellings
    currentWordInPartEntrySpellingContainers.forEach(currentWordInPartEntrySpellingContainer => {
      currentWordInPartEntrySpellingContainer.classList.add('collapse');
      this.resetEnrtyCategoryWordSpellingLinkages(currentWordInPartEntrySpellingContainer);
    });
  }

  // this method hides in_parts words spellings which are not in_parts words of current word 
  hideOtherEntryCategoryInPartsWordsSpellingsFromLinkageModal(spellingField) {
    const inPartEntrySpellingContainers = spellingField.querySelectorAll('.js-entry-part-spelling');
    
    // hide all other in_parts words spellings that are not in_parts words of current word 
    inPartEntrySpellingContainers.forEach(inPartEntrySpellingContainer => {
      inPartEntrySpellingContainer.classList.add('collapse');
      this.resetEnrtyCategoryWordSpellingLinkages(inPartEntrySpellingContainer);
    });
  }

  // this method hides multi words spellings 
  hideMultiWordsSpellingsFromLinkageModal(spellingField) {
    if (!this.isWordCategoryEntry) return; // Early return if word type is not Entry

    const multiWordContainers = spellingField.querySelectorAll('.js-entry-multi-spelling');

    multiWordContainers.forEach(multiWordContainer => {
      multiWordContainer.classList.add('collapse');
      this.resetEnrtyCategoryWordSpellingLinkages(multiWordContainer);
    });
  }

  // this method displays multi words spellings 
  displayMultiWordsSpellingsFromLinkageModal(spellingField) {
    const multiWordContainers = spellingField.querySelectorAll('.js-entry-multi-spelling');
        
    multiWordContainers.forEach(multiWordContainer => {
      multiWordContainer.classList.remove('collapse');
      this.updateDestroyField(multiWordContainer, false);
    });
  }


  // this method displays in_part spellings in current spelling field linkages modal
  displayAllInPartSpellingsFromLinkageModal(spellingField) {
    this.displayCurrentWordInPartsWordsSpellingsFromLinkageModal(spellingField);
    this.displayOtherEntryCategoryInPartsWordsSpellingsFromLinkageModal(spellingField);
  }

  // this method displays current word in_parts words spellings
  displayCurrentWordInPartsWordsSpellingsFromLinkageModal(spellingField) {
    const currentWordInPartEntrySpellingContainers = spellingField.querySelectorAll('.js-current-word-part-spelling');

    // display current word in_parts words spellings
    currentWordInPartEntrySpellingContainers.forEach(currentWordInPartEntrySpellingContainer => {
      currentWordInPartEntrySpellingContainer.classList.remove('collapse');
      this.updateDestroyField(currentWordInPartEntrySpellingContainer, false);
    });
  }

  // this method displays in_parts words  spellings which are not in_parts words of current word 
  displayOtherEntryCategoryInPartsWordsSpellingsFromLinkageModal(spellingField) {
    const inPartEntrySpellingContainers = spellingField.querySelectorAll('.js-entry-part-spelling');
    
    // display all other in_parts words spellings that are not in_parts words of current word 
    inPartEntrySpellingContainers.forEach(inPartEntrySpellingContainer => {
      // TODO: Verify/test this condition
      inPartEntrySpellingContainer.classList.remove('collapse');
      this.updateDestroyField(inPartEntrySpellingContainer, false)
    });
  }

  resetEnrtyCategoryWordSpellingLinkages(spellingField){
    // Reset sources
    this.resetEntryWordLinkageSources(spellingField);
    
    // Reset Toggles
    this.resetEnrtyWordSpellingLinkageToggles(spellingField);

    // Mark destroy hidden field to true
    this.updateDestroyField(spellingField, true);
  }

  
  resetEntryWordLinkageSources(entryWordSpellingLinkage){
    const sourcesLinkages = entryWordSpellingLinkage.querySelectorAll('.js-latin-history-modal');
    sourcesLinkages.forEach(sourcesLinakge => {
      // Reset url fields
      const sourcesUrlsFields = sourcesLinakge.querySelectorAll('.js-source-url-input');
      sourcesUrlsFields.forEach(sourcesUrlField => {
        sourcesUrlField.value = '';
      });

      // Reset page no. fields
      const sourcesPageNoFields = sourcesLinakge.querySelectorAll('.js-source-page-no-input');
      sourcesPageNoFields.forEach(sourcesPageNoField => {
        sourcesPageNoField.value = '';
      });
    });
  }

  resetEnrtyWordSpellingLinkageToggles(entryWordSpellingLinkage, resetNearSynonymToggle = false){
    const relationshipToggles = entryWordSpellingLinkage.querySelectorAll('input[type="checkbox"]');
      
    relationshipToggles.forEach(relationshipToggle => {
      // Preserve the value of the alternative toggle
      if (resetNearSynonymToggle || !(relationshipToggle.classList.contains('js-marked-as-near-synonym-toggle') && relationshipToggle.disabled == true)) {
        relationshipToggle.checked = false;
      }
    });
  }

  updateDestroyField(spellingField, markAsDestroyed) {
    const hiddenDestroyField = spellingField?.querySelector('.js-destroy-component-spelling-linkage');
    
    if(hiddenDestroyField){
      hiddenDestroyField.value = markAsDestroyed;
    }
  }

  isCurrentWordMisspelled() {
    let isMisspelled = false;

    // For individual words, the misspelledCheckBoxTarget is available since the misspelled toggle exists.
    // For combined words, there is no misspelled toggle, so the checkbox target is not present.
    if (typing_form?.hasMisspelledCheckBoxTarget) {
      isMisspelled = typing_form.misspelledCheckBoxTarget.checked;
    } else if (this.typing_for === 'combined_word') {
      isMisspelled =  this.isWordMisspelled;
    }

    return isMisspelled;
  }

  // Active Type-O stack lane: corrected when misspelled, otherwise uncorrected primary/alternates.
  activeSpellingLane() {
    return this.isCurrentWordMisspelled() ? 'corrected' : 'uncorrected';
  }

  // Returns spelling fields for the active stack lane (corrected when misspelled, primary otherwise).
  allSpellingFields(){
    return this.spellingFieldsForLane(this.activeSpellingLane());
  }

  // CPG-252: Morph toggles gate within their own lane — uncorrected primary stack vs corrected stack.
  morphologicalLaneForField(field) {
    return field.classList.contains('js-corrected-entries-input') ? 'corrected' : 'uncorrected';
  }

  spellingFieldsForLane(lane) {
    const selector = lane === 'corrected'
      ? '.js-corrected-entries-input'
      : '.js-uncorrected-entry-input';

    return [...document.querySelectorAll(selector)]
      .filter(el => el.offsetParent !== null && !el.classList.contains('d-none'));
  }

  stackOrderedSpellingFieldsForLane(lane) {
    const fields = this.spellingFieldsForLane(lane);

    return fields.slice().sort((fieldA, fieldB) => {
      if (fieldA === fieldB) return 0;

      const position = fieldA.compareDocumentPosition(fieldB);

      if (position & Node.DOCUMENT_POSITION_FOLLOWING) return -1;
      if (position & Node.DOCUMENT_POSITION_PRECEDING) return 1;

      return this.orderNumberOfSpellingField(fieldA) - this.orderNumberOfSpellingField(fieldB);
    });
  }

  closestBelowFieldInLane(currentField, lane) {
    return this.domAdjacentBelowFieldInLane(currentField, lane) ||
      this.orderNumberClosestBelowFieldInLane(currentField, lane);
  }

  closestAboveFieldInLane(currentField, lane) {
    return this.domAdjacentAboveFieldInLane(currentField, lane) ||
      this.orderNumberClosestAboveFieldInLane(currentField, lane);
  }

  domAdjacentBelowFieldInLane(currentField, lane) {
    const orderedFields = this.stackOrderedSpellingFieldsForLane(lane);
    const currentIndex = orderedFields.indexOf(currentField);

    if (currentIndex === -1 || currentIndex === orderedFields.length - 1) return null;

    return orderedFields[currentIndex + 1];
  }

  domAdjacentAboveFieldInLane(currentField, lane) {
    const orderedFields = this.stackOrderedSpellingFieldsForLane(lane);
    const currentIndex = orderedFields.indexOf(currentField);

    if (currentIndex <= 0) return null;

    return orderedFields[currentIndex - 1];
  }

  orderNumberClosestBelowFieldInLane(currentField, lane) {
    const currentFieldOrderNumber = this.orderNumberOfSpellingField(currentField);
    const allFields = this.spellingFieldsForLane(lane);

    let closestBelowSpelling = null;
    let closestBelowDiff = Infinity;

    allFields.forEach((spellingField) => {
      const spellingFieldOrderNumber = this.orderNumberOfSpellingField(spellingField);

      if (spellingFieldOrderNumber > currentFieldOrderNumber) {
        const diff = spellingFieldOrderNumber - currentFieldOrderNumber;

        if (diff < closestBelowDiff) {
          closestBelowSpelling = spellingField;
          closestBelowDiff = diff;
        }
      }
    });

    return closestBelowSpelling;
  }

  orderNumberClosestAboveFieldInLane(currentField, lane) {
    const currentFieldOrderNumber = this.orderNumberOfSpellingField(currentField);
    const allFields = this.spellingFieldsForLane(lane);

    let closestAboveSpelling = null;
    let closestAboveDiff = Infinity;

    allFields.forEach((spellingField) => {
      const spellingFieldOrderNumber = this.orderNumberOfSpellingField(spellingField);

      if (spellingFieldOrderNumber < currentFieldOrderNumber) {
        const diff = Math.abs(spellingFieldOrderNumber - currentFieldOrderNumber);

        if (diff < closestAboveDiff) {
          closestAboveSpelling = spellingField;
          closestAboveDiff = diff;
        }
      }
    });

    return closestAboveSpelling;
  }

  orderNumberOfSpellingField(spellingField){
    if (!spellingField) return;

    // Get the hidden field that stores the order number of this spelling field
    const hiddenFieldForOrderNumberOfSpellingField = spellingField.querySelector('.js-otto-spelling-assigned-number');
    if (!hiddenFieldForOrderNumberOfSpellingField) return;

    // Get the order number of the spelling field
    const spellingFieldOrderNumber = parseInt(hiddenFieldForOrderNumberOfSpellingField.value, 10);

    return spellingFieldOrderNumber;
  }

  // Returns the value card directly above currentField in the active stack lane.
  closestAboveField(currentField){
    return this.closestAboveFieldInLane(currentField, this.activeSpellingLane());
  }

  // All visible spelling fields in the active lane, sorted top-to-bottom.
  stackOrderedSpellingFields() {
    return this.stackOrderedSpellingFieldsForLane(this.activeSpellingLane());
  }

  // The single card immediately above currentField in the active lane, or null if first/missing.
  domAdjacentAboveField(currentField) {
    return this.domAdjacentAboveFieldInLane(currentField, this.activeSpellingLane());
  }

  // Fallback when DOM order is stale — nearest card above by assigned order number in the active lane.
  orderNumberClosestAboveField(currentField){
    return this.orderNumberClosestAboveFieldInLane(currentField, this.activeSpellingLane());
  }

  // --- CPG-252: Infl./Drvl. relationship toggles on Value Cards (Type-O only) ---
  //
  // Each value card has Infl. and Drvl. toggles gated by:
  //   Section 3 — enabled only when the card directly below has Root active.
  //   Section 4 — disabled only on parents that actually have 2+ immediate parallel
  //               Root children (compound splits linked with arrows). Stack position alone
  //               never makes a card a parallel-root parent — consecutive Root cards are a
  //               plain hierarchy and keep their Section 3 gating.
  //   Section 7 — silently uncheck and disable when gating is lost on reorder/add/remove/Root off.

  handleMorphologicalToggleExclusivity(e) {
    const currentToggle = e.currentTarget;
    if (!currentToggle.checked || currentToggle.disabled) return;

    const togglesContainer = currentToggle.closest('.js-value-card-left-margin-container');
    if (!togglesContainer) return;

    const inflectionalToggle = togglesContainer.querySelector('.js-morphological-inflectional-toggle');
    const derivationalToggle = togglesContainer.querySelector('.js-morphological-derivational-toggle');

    if (currentToggle === inflectionalToggle && derivationalToggle?.checked) {
      derivationalToggle.checked = false;
    } else if (currentToggle === derivationalToggle && inflectionalToggle?.checked) {
      inflectionalToggle.checked = false;
    }
  }

  // Walk every value card and apply Section 3/4/7 gating to its Infl./Drvl. toggles.
  // Uncorrected and corrected stacks gate independently when misspelled.
  updateMorphologicalToggleStates({ lane = null } = {}) {
    const morphToggleFields = document.querySelectorAll('#typeo-fields-container .js-spelling-field-container');

    morphToggleFields.forEach((field) => {
      const inflectionalToggle = field.querySelector('.js-morphological-inflectional-toggle');
      const derivationalToggle = field.querySelector('.js-morphological-derivational-toggle');

      if (!inflectionalToggle && !derivationalToggle) return;

      const morphLane = this.morphologicalLaneForField(field);
      if (lane && morphLane !== lane) return;

      const laneStackFields = new Set(this.spellingFieldsForLane(morphLane));
      const parallelRootParents = this.parallelRootParentFields(morphLane);

      const isFieldVisible = field.offsetParent !== null && !field.classList.contains('d-none');
      const isInLaneStack = laneStackFields.has(field);
      const gatingAllows = isFieldVisible &&
        isInLaneStack &&
        this.isMorphologicalToggleEnabledForField(field, parallelRootParents, morphLane);

      if (!isInLaneStack || !isFieldVisible) {
        this.applyMorphologicalToggleEnabledState(inflectionalToggle, derivationalToggle, false, { preserveChecked: true });
      } else {
        this.applyMorphologicalToggleEnabledState(inflectionalToggle, derivationalToggle, gatingAllows);
      }
    });
  }

  applyMorphologicalToggleEnabledState(inflectionalToggle, derivationalToggle, isEnabled, { preserveChecked = false } = {}) {
    [inflectionalToggle, derivationalToggle].forEach((toggle) => {
      if (!toggle) return;

      toggle.disabled = !isEnabled;
      toggle.closest('.morphological-inflectional-toggle-container, .morphological-derivational-toggle-container')?.classList.toggle('morphological-toggle-disabled', !isEnabled);

      if (!isEnabled && toggle.checked && !preserveChecked) {
        toggle.checked = false;
      }
    });
  }

  // Debounce toggle-state refresh so rapid stack changes coalesce into a single pass.
  scheduleMorphologicalToggleStatesUpdate({ lane = null } = {}) {
    if (this.morphologicalToggleUpdateTimeout) {
      clearTimeout(this.morphologicalToggleUpdateTimeout);
    }

    this.morphologicalToggleUpdateLane = lane;

    this.morphologicalToggleUpdateTimeout = setTimeout(() => {
      this.updateMorphologicalToggleStates({ lane: this.morphologicalToggleUpdateLane });
      this.morphologicalToggleUpdateLane = null;
      this.morphologicalToggleUpdateTimeout = null;
    }, 0);
  }

  isMorphologicalToggleEnabledForField(field, parallelRootParents = null, lane = null) {
    const morphLane = lane || this.morphologicalLaneForField(field);
    const parents = parallelRootParents || this.parallelRootParentFields(morphLane);

    if (parents.has(field)) return false;

    const cardBelow = this.closestBelowFieldInLane(field, morphLane);
    if (!cardBelow || !this.isRootActive(cardBelow)) return false;

    return true;
  }

  // A card is a parallel-root parent only when 2+ Root cards name it as their immediate
  // explanation. Adjacency can yield at most one such child, so parallel roots always come
  // from arrow linkages — a straight run of Root cards is a hierarchy, not a compound split.
  parallelRootParentFields(lane) {
    const parents = new Set();

    this.spellingFieldsForLane(lane).forEach((field) => {
      if (this.getImmediateParallelRootChildren(field, lane).length >= 2) {
        parents.add(field);
      }
    });

    return parents;
  }

  getImmediateExplanationField(field, lane) {
    const explanationTempId = field.querySelector('.js-explanation-spelling-temp-reference-id')?.value;

    if (!explanationTempId) {
      return this.domAdjacentAboveFieldInLane(field, lane);
    }

    return this.findFieldByTempId(explanationTempId, lane);
  }

  getImmediateParallelRootChildren(parentField, lane) {
    return this.spellingFieldsForLane(lane).filter((field) => {
      if (!this.isRootActive(field)) return false;

      return this.getImmediateExplanationField(field, lane) === parentField;
    });
  }

  isRootActive(field) {
    return typing_form?.getCurrentOttoFieldToggle(field, 'rootToggle')?.checked === true;
  }

  findFieldByTempId(tempId, lane) {
    if (!tempId) return null;

    return this.spellingFieldsForLane(lane).find((field) => {
      return field.querySelector('.js-spelling-temp-reference-id')?.value === tempId;
    }) || null;
  }

  languageToggleValuesForField(spellingField){
    return {
      turkish: spellingField.querySelector('.js-marked-as-turkish-toggle')?.checked || false,
      persian: spellingField.querySelector('.js-marked-as-persian-toggle')?.checked || false,
      arabic:  spellingField.querySelector('.js-marked-as-arabic-toggle')?.checked  || false,
    };    
  }

  setLanguageTogglesOfNewAddedField(newFieldCreated) {
    if (typing_form.typing_stage === 'type_l') return;

    const closestAboveSpellingField        = this.closestAboveField(newFieldCreated);
    const languageToggleValuesOfAboveField =  this.languageToggleValuesForField(closestAboveSpellingField);
   
    const turkishToggle = newFieldCreated.querySelector('.js-marked-as-turkish-toggle');
    const persianToggle = newFieldCreated.querySelector('.js-marked-as-persian-toggle');
    const arabicToggle  = newFieldCreated.querySelector('.js-marked-as-arabic-toggle');

    if (turkishToggle) turkishToggle.checked = languageToggleValuesOfAboveField.turkish;
    if (persianToggle) persianToggle.checked = languageToggleValuesOfAboveField.persian;
    if (arabicToggle)  arabicToggle.checked  = languageToggleValuesOfAboveField.arabic;
  }

  updateDestroyValueOFAllEntrySpellingLinkagesOfSelectedContainer(selectedSpellingFieldContainer, destroyFlag){
    const currentFieldlinkagesDestroyFields = selectedSpellingFieldContainer.querySelectorAll('.js-destroy-component-spelling-linkage')
    
    currentFieldlinkagesDestroyFields.forEach(currentFieldlinkagesDestroyField => {
      currentFieldlinkagesDestroyField.value = destroyFlag;
    });
  }

  spellingsHaveSamePointsAndOttomanValues(explainedSpellingValue, explainedSpellingPoints, explanationSpellingValue, explanationSpellingPoints){
    return (
      this.hasSameOttomanExceptEndingWithHaa(explainedSpellingValue, explanationSpellingValue) &&
      this.arePointsSame(explainedSpellingPoints, explanationSpellingPoints)    
    );
  }

  arePointsSame(points1, points2) {
    // Checking if either of the point arrays is empty
    if (!points1.length || !points2.length) {
      return false;
    }

    // Compare each polygon in points1 with each polygon in points2
    for (let polygon1 of points1) {
      for (let polygon2 of points2) {
        let pointsSet1 = polygon1.map(p => [p.x, p.y]).sort();
        let pointsSet2 = polygon2.map(p => [p.x, p.y]).sort();

        // If the sets have the same size and the points match after sorting, return true
        if (pointsSet1.length === pointsSet2.length && JSON.stringify(pointsSet1) === JSON.stringify(pointsSet2)) {
          return true;
        }
      }
    }

    // If no match is found, return false
    return false;
  }

  // Sync Same toggle state for all linkage containers
  updateSameToggleStateForLinkageContainers(spellingContainer, currentOttomanValue) {
    // Only proceed if current word is not an entry (non-entry words)
    if (this.isWordCategoryEntry) return;

    // Only proceed if there's a current Ottoman value
    if (!currentOttomanValue || currentOttomanValue.trim() === '') return;

    // Find all spelling linkage containers in the molecule panel
    const spellingLinkageContainers = spellingContainer.querySelectorAll('.js-spelling-linkages-container:not(.collapse)');

    spellingLinkageContainers.forEach(linkageContainer => {
      // Get the entry word spelling from the linkage container
      const entryWordSpelling = linkageContainer.querySelector('.js-entry-word-spelling');
      if (!entryWordSpelling) return;

      const entryOttomanValue = entryWordSpelling.textContent.trim();
      if (!entryOttomanValue) return;

      // Check if Ottoman spellings match (using existing comparison logic)
      const spellingsMatch = this.hasSameOttomanExceptEndingWithHaa(currentOttomanValue, entryOttomanValue);

      // Find the Same toggle in this linkage container
      const sameToggle = linkageContainer.querySelector('.js-marked-as-same-toggle');
      if (!sameToggle) return;

      const isNewRecord     = linkageContainer.dataset.isNewRecord === 'true';
      const userInteracted  = linkageContainer.dataset.userSameToggled === 'true';

      if (isNewRecord && !userInteracted) {
        let toggled = false;
        if (spellingsMatch && !sameToggle.checked) {
          sameToggle.checked = true;
          sameToggle.dispatchEvent(new Event('change', { bubbles: true }));
          toggled = true;
        } else if (!spellingsMatch && sameToggle.checked) {
          // Auto-uncheck if values no longer match
          sameToggle.checked = false;
          sameToggle.dispatchEvent(new Event('change', { bubbles: true }));
          toggled = true;
        }

        // If we toggled programmatically, refresh the molecule icon color on the owning spelling field
        if (toggled) {
          const spellingField = this.currentTypingFieldContainer(linkageContainer);
          if (spellingField) this.updateMoleculeIconColor(spellingField);
        }
      }
    });
  }

  // Handle keystrokes on ottoman inputs (bound from markup)
  syncSameToggleInLinkagePannel(e = null) {
    if (this.isWordCategoryEntry) return;

    if (e){
      // Only for primary or component spellings
      const spellingContainer = this.currentTypingFieldContainer(e.target);
      const isPrimarySpelling = spellingContainer?.querySelector('.js-marked-as-primary-radio-btn')?.checked === true;
      const isComponent       = spellingContainer?.querySelector('.js-marked-as-component-checkbox')?.checked === true;

      if (!(isPrimarySpelling || isComponent)) return;
      
      const currentOttomanValue = spellingContainer.querySelector('.js-spelling-field')?.value;

      this.updateSameToggleStateForLinkageContainers(spellingContainer, currentOttomanValue);      
    } else {
      // const spellingFields = this.currentWordTypeTarget.querySelectorAll('.js-entries-input.js-spelling-field-container') || [];
      const spellingFields = this.allSpellingFields();

      spellingFields.forEach(spellingField => {
        const isPrimarySpelling = spellingField.querySelector('.js-marked-as-primary-radio-btn')?.checked === true;
        const isComponent       = spellingField.querySelector('.js-marked-as-component-checkbox')?.checked === true;

        if (!(isPrimarySpelling || isComponent)) return;

        const value = spellingField.querySelector('.js-spelling-field')?.value;
        if (!value || value.trim() === '') return;

        this.updateSameToggleStateForLinkageContainers(spellingField, value);
      });
    }
  }

  updateUnrelatedToggleStateForLinkageContainers(spellingContainer, currentOttomanValue) {
    if (this.isWordCategoryEntry) return;

    const value = (currentOttomanValue || '').trim();

    const unrelated_toggle_trigger_words = new Set([
      'و','بو','ایسە','ایسه','اما','لكن','چونكى','زيرا','كى','ده','دە','دا','ايله','ايچون','قدر','گورە','گوره','كورە','كوره','اوزرە','اوزره','كبى','بيلى','شو','او','بن','سن','او','بيز','سيز','اونلار','هم','يا','اگر','اكر','شايد'
    ]);

    const spellingLinkageContainers = spellingContainer.querySelectorAll('.js-spelling-linkages-container:not(.collapse)');
    
    spellingLinkageContainers.forEach(linkageContainer => {
      // Ensure there is an entry spelling node (same pattern as Same)
      const entryWordSpelling = linkageContainer.querySelector('.js-entry-word-spelling');
      if (!entryWordSpelling) return;

      const unrelatedToggle = linkageContainer.querySelector('.js-marked-as-unrelated-value-toggle');
      if (!unrelatedToggle) return;

      const isNewRecord    = linkageContainer.dataset.isNewRecord === 'true';
      const userInteracted = linkageContainer.dataset.userUnrelatedToggled === 'true';

      if (!isNewRecord || userInteracted) return;

      const triggerUnrelatedToggle = unrelated_toggle_trigger_words.has(value);
      let toggled = false;

      if (triggerUnrelatedToggle && !unrelatedToggle.checked) {
        unrelatedToggle.checked = true;
        unrelatedToggle.dispatchEvent(new Event('change', { bubbles: true }));
        toggled = true;
      } else if (!triggerUnrelatedToggle && unrelatedToggle.checked) {
        unrelatedToggle.checked = false;
        unrelatedToggle.dispatchEvent(new Event('change', { bubbles: true }));
        toggled = true;
      }

      if (toggled) {
        const spellingField = this.currentTypingFieldContainer(linkageContainer);
        if (spellingField) this.updateMoleculeIconColor(spellingField);
      }
    });
  }

  syncUnrelatedToggleInLinkagePannel(e = null) {
    if (this.isWordCategoryEntry) return;

    if (e) {
      // Only for primary or component spellings
      const spellingContainer = this.currentTypingFieldContainer(e.target);
      const isPrimarySpelling = spellingContainer?.querySelector('.js-marked-as-primary-radio-btn')?.checked === true;
      const isComponent       = spellingContainer?.querySelector('.js-marked-as-component-checkbox')?.checked === true;

      if (!(isPrimarySpelling || isComponent)) return;

      const currentOttomanValue = spellingContainer.querySelector('.js-spelling-field')?.value;

      this.updateUnrelatedToggleStateForLinkageContainers(spellingContainer, currentOttomanValue);
    } else {
      const spellingFields = this.allSpellingFields();

      spellingFields.forEach(spellingField => {
        const isPrimarySpelling = spellingField.querySelector('.js-marked-as-primary-radio-btn')?.checked === true;
        const isComponent = spellingField.querySelector('.js-marked-as-component-checkbox')?.checked === true;

        if (!(isPrimarySpelling || isComponent)) return;

        const value = spellingField.querySelector('.js-spelling-field')?.value;
        if (!value || value.trim() === '') return;

        this.updateUnrelatedToggleStateForLinkageContainers(spellingField, value);
      });      
    }
  }

  getVisibleOttomanFields(container) {
    return Array.from(container.querySelectorAll('.js-entries-input'))
      .filter(el => el.offsetParent !== null && !el.classList.contains('d-none'));
  }

  handleAuthorLangTogglesVisibilityOnAlternates() {
    if (!this.hasCurrentWordTypeTarget) return;

    // Collect all visible spelling fields
    const allVisibleFields   = this.allSpellingFields();
    const visibleFieldsCount = allVisibleFields.length;
    if (!visibleFieldsCount) return;

    // TODO: Create seperate method to pick C.Fr. toggle because this is also used in handleCombineFragmentsButtonVisibility method.
    const firstOttoField             = allVisibleFields[0];
    const compoundFragmentToggle     = firstOttoField?.querySelector('.js-marked-as-compound-fragment-visual-toggle');
    const isCompoundFragmentToggleOn = compoundFragmentToggle && compoundFragmentToggle.checked === true;

    // Show author-specified toggles also on the Second field (and Third if compound ON and enough fields)
    allVisibleFields.forEach((field, index) => {
      var showToggles = false;

      if (isCompoundFragmentToggleOn && visibleFieldsCount >= 3) {
        // Show on index 1 and 2 when compound is ON and there are 3+ fields
        showToggles = (index === 1 || index === 2);
      } else { // Otherwise, show only on primary and components
        // Check if this field is marked as primary
        const primaryRadioBtn = field.querySelector('.js-marked-as-primary-radio-btn');
        const isPrimary       = primaryRadioBtn && primaryRadioBtn.checked === true;
        
        const isComponent     = this.isComponentSpelling(field);
        
        // Show toggles only if field is marked as primary OR component
        showToggles = index === 0 || isPrimary || isComponent
      }

      field.querySelectorAll('.js-author-toggles-container').forEach(container => {
        if (showToggles) {
          container.classList.remove('d-none');
        } else {
          container.classList.add('d-none');
          container.querySelectorAll('.typeo-language-toggle-input').forEach(checkbox => {
            checkbox.checked = false;
          });
        }
      });
    });
  }

  combineCompoundFragments(e) {
    const combineFragmentsBtn  = e.target;
    const typingFieldContainer = combineFragmentsBtn.closest('.js-typing-value-container');
    if (!typingFieldContainer) return;

    const typeoFieldsRootContainer = document.getElementById('typeo-fields-container');
    if (!typeoFieldsRootContainer) return;

    // 1) Read fragments array from data attribute
    let compoundFragments = [];
    compoundFragments = JSON.parse(typeoFieldsRootContainer.dataset.compoundFragmentsOttomanValues || '[]');

    // 2) Read current page’s first ottoman value (the top field of current typing container)
    const ottoInputFields     = this.getVisibleOttomanFields(typingFieldContainer);
    const firstOttoFieldValue = ottoInputFields[0]?.querySelector('.js-text-field')?.value || '';

    // 3) Build combined text: fragments + primary (primary appended at the end)
    const parts = [...compoundFragments];
    if (firstOttoFieldValue) parts.push(firstOttoFieldValue);
    const combinedValue = parts.join(' ').trim();
    if (!combinedValue) return;

    // 4) Click "+ Other Value" to generate the next field
    typingFieldContainer.querySelector('.js-btn-add-alternate').click();

    // 5) After field is inserted, populate the newly created field
    let alternateTextFields           = typingFieldContainer.querySelectorAll('.js-alternate-ottoman-spelling-text-field');
    let focusedAlternativeTextField   = alternateTextFields[alternateTextFields.length - 1];
    focusedAlternativeTextField.value = combinedValue;

    // 6) Clear Root on this new field only (template defaults Root ON for "+ Other Value").
    // Silent uncheck — no change event — so shared Root handlers / other features are untouched.
    const newAlternateFieldContainer = focusedAlternativeTextField.closest('.js-entries-input');
    const rootToggle = newAlternateFieldContainer?.querySelector('.js-marked-as-root-toggle');
    if (rootToggle) {
      rootToggle.checked = false;
    }

    // 7) Mark the newly created alternate as primary (if its radio is visible)
    const primaryRadio = newAlternateFieldContainer?.querySelector('.js-marked-as-primary-radio-btn');

    if (primaryRadio) {
      setTimeout(() => {
        primaryRadio.checked = true;
        primaryRadio.dispatchEvent(new Event('click', { bubbles: true }));
      }, 100);
    }
  }

  // Populates ONLY the current word's primary field based on the same qualification logic used server-side:
  // - derive current_word's index from the list of words sharing the same points, ordered by assigned_number
  // - choose entry-word spelling at that index, clamped to the last available entry word
  handleEmDashToggleStateChange(e) {
    const isEmDashToggleOn = e.currentTarget.checked;
    const isMisspelled =
      typing_form.hasMisspelledCheckBoxTarget &&
      typing_form.misspelledCheckBoxTarget.checked;

    const targetContainer = document.getElementById(
      isMisspelled
        ? 'js-corrected-otto-fields-container'
        : 'js-primary-otto-fields-container'
    );

    const emDashHiddenFieldContainer = document.getElementById('js-primary-em-dash-hidden-field-container');

    if (!targetContainer || !emDashHiddenFieldContainer) return;

    // Toggle visibility
    emDashHiddenFieldContainer.classList.toggle('d-none', !isEmDashToggleOn);

    // ---------- EmDash OFF logic ----------
    // When em-dash is switched off, clear the current field by triggering the same destroy action
    // used elsewhere in the form.
    if (!isEmDashToggleOn) {
      const visibleFields = this.getVisibleOttomanFields(targetContainer);
      const firstField = visibleFields[0];
      firstField?.querySelector('.js-typeo-field-destroy-btn')?.click();
      return;
    }

    // ---------- EmDash ON logic ----------
    const typeoFieldsContainer = document.getElementById('typeo-fields-container');
    if (!typeoFieldsContainer?.dataset.paragraphWordsFirstOttomanValues) return;

    let entryWordsSpellings;
    try {
      entryWordsSpellings = JSON.parse(typeoFieldsContainer.dataset.paragraphWordsFirstOttomanValues);
    } catch {
      return;
    }
    if (!entryWordsSpellings?.length) return;

    const currentWordIdRaw = typeoFieldsContainer.dataset.currentWord;
    const currentWordId    = parseInt(currentWordIdRaw, 10);
    if (!currentWordId || Number.isNaN(currentWordId)) return;

    $.ajax({
      url: '/page_set/typeo/get_em_dash_word_position',
      type: 'GET',
      data: { word_id: currentWordId },
      success: (response) => {
        const positionRaw  = response?.position_of_word;
        let positionOfWord = parseInt(positionRaw, 10);
        if (Number.isNaN(positionOfWord) || positionOfWord < 0) positionOfWord = 0;

        // if there are fewer entry words than em-dash position, use the last entry word spelling
        const entryWordIndex = Math.min(positionOfWord, entryWordsSpellings.length - 1);
        const qualifyingEntryWordSpelling = entryWordsSpellings[entryWordIndex];
        const valueOttoman = qualifyingEntryWordSpelling?.value_ottoman || '';
        if (!valueOttoman) return;

        const visibleFields = this.getVisibleOttomanFields(targetContainer);
        const firstField = visibleFields[0];
        const primaryInput = firstField?.querySelector('.js-text-field');
        if (!primaryInput) return;

        primaryInput.value = valueOttoman;
      }
    });
  }

  // Manually copy Ottoman data from the latest saved same-points duplicate onto the current word.
  copyFromDuplicateRegion(event) {
    const wordId    = event.currentTarget.dataset.wordId;
    const pageSetId = event.currentTarget.dataset.pageSetId;

    $.ajax({
      url: '/page_set/typeo/copy_from_duplicate_region',
      type: 'POST',
      data: { word_id: wordId, page_set: pageSetId },
      success: (response) => {
        if (response.success) {
          window.location.href = response.redirect_url;
        } else {
          this.addFlashMessage(response.error);
        }
      }
    });
  }
}